/**
 * Article Detail Page - Centralized Component
 * 
 * This component dynamically loads and renders article components based on URL parameter.
 * Similar architecture to ToolDetailPage.tsx for calculators.
 * 
 * Benefits:
 * - Single file instead of 44 individual page files
 * - Automatic Schema.org structured data injection
 * - Consistent breadcrumb navigation
 * - Easy to add new articles (just update articles.config.ts)
 * - Instant navigation (no lazy loading)
 * 
 * Usage:
 * Route: /:lang/resources/:id
 * Example: /en/resources/compound-interest-article
 * → Loads CompoundInterestArticle component
 * → Injects Article + Breadcrumb schemas
 * 
 * Architecture:
 * URL Param (:id) → ARTICLE_CONFIGS[id] → Component + Metadata → Schema Injection
 */

import { useParams, useLocation } from 'react-router-dom';
import { Suspense } from 'react';
import NotFound from './not-found';
import { useI18n } from '../i18n/i18n';
import { useResourceSEO } from '../hooks/useSEO';
import StructuredData from '../components/StructuredData';
import { createArticleSchema, createBreadcrumbSchema } from '../lib/schema-factory';
import { ARTICLE_CONFIGS, type ArticleConfig } from '../config/articles.config';

/**
 * Article Detail Page Component
 */
const ArticleDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { t } = useI18n();

  // Get article configuration
  const articleConfig: ArticleConfig | undefined = id ? ARTICLE_CONFIGS[id] : undefined;

  // If article doesn't exist, show 404
  if (!articleConfig) {
    return <NotFound />;
  }

  // SEO Configuration - uses centralized metadata from seo.config.ts
  // with fallback to article-specific data for additional metadata
  const seoContent = useResourceSEO(id || '', {
    ogType: 'article',
    publishedTime: articleConfig.datePublished,
    modifiedTime: articleConfig.dateModified,
    author: 'Finanzly Team',
    section: 'Finanzas Personales',
    tags: articleConfig.keywords,
    ogImage: articleConfig.imageUrl,
  });

  // Create Article Schema.org structured data
  const articleSchema = createArticleSchema({
    headline: t(`meta.${articleConfig.metaKey}.title`),
    description: t(`meta.${articleConfig.metaKey}.description`),
    url: `/resources/${articleConfig.id}`,
    datePublished: articleConfig.datePublished,
    dateModified: articleConfig.dateModified,
    keywords: articleConfig.keywords,
    image: articleConfig.imageUrl, // Use 'image' parameter name
  });

  // Create Breadcrumb Schema.org structured data
  const breadcrumbSchema = createBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Resources', url: '/resources' },
    { name: t(`meta.${articleConfig.metaKey}.title`), url: location.pathname },
  ]);

  // Get the article component
  const ArticleComponent = articleConfig.component;

  return (
    <section className="bg-white py-16 fade-in">
      {seoContent}
      {/* Article Schema.org structured data */}
      <StructuredData schema={articleSchema} />
      
      {/* Breadcrumb Schema.org structured data */}
      <StructuredData schema={breadcrumbSchema} />
      
      {/* Render article component with Suspense for lazy loading */}
      <Suspense fallback={
        <div className="container mx-auto px-4 py-12 text-center">
          <div className="animate-pulse">
            <div className="h-8 bg-neutral-200 rounded w-3/4 mx-auto mb-4"></div>
            <div className="h-4 bg-neutral-200 rounded w-1/2 mx-auto mb-8"></div>
            <div className="space-y-3">
              <div className="h-4 bg-neutral-200 rounded"></div>
              <div className="h-4 bg-neutral-200 rounded"></div>
              <div className="h-4 bg-neutral-200 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      }>
        <ArticleComponent />
      </Suspense>

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }

          .fade-in {
            animation: fadeIn 0.5s ease forwards;
          }
        `}
      </style>
    </section>
  );
};

export default ArticleDetailPage;
